import { useCallback } from 'react'
import { useMindMapContext } from '../../../../context/mindmap'
import { MindmapService } from '../../../../services/mindmap/mindmapService'
import type { VentureNodeData } from '../../../../types/mindmap-components'

/**
 * Hook for VentureNode-specific update handlers.
 * Updates venture fields directly on the mindmap node and persists them via the mindmap JSON save flow.
 */
export function useVentureNodeUpdates() {
  const { setNodes, setHasUnsavedChanges, mindmapId, setMindmapTitle } = useMindMapContext()

  /**
   * Generic field updater for venture node data
   */
  const updateVentureField = useCallback(
    (nodeId: string, nodeField: keyof VentureNodeData, value: string) => {
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === nodeId && node.data.category === 'venture') {
            return {
              ...node,
              data: {
                ...node.data,
                [nodeField]: value,
                userModified: true,
              },
            }
          }
          return node
        })
      )
      setHasUnsavedChanges(true)
    },
    [setNodes, setHasUnsavedChanges]
  )

  const updateVentureName = useCallback(
    (nodeId: string, name: string) => {
      updateVentureField(nodeId, 'companyName', name)
      // Sync venture name → project title (single source of truth)
      setMindmapTitle(name)
      if (mindmapId) {
        void MindmapService.update(mindmapId, { title: name })
      }
    },
    [updateVentureField, setMindmapTitle, mindmapId]
  )

  const updateBusinessModel = useCallback(
    (nodeId: string, value: string) => {
      updateVentureField(nodeId, 'businessModelAndValueProp', value)
    },
    [updateVentureField]
  )

  const updateCapabilities = useCallback(
    (nodeId: string, value: string) => {
      updateVentureField(nodeId, 'capabilitiesAndAssets', value)
    },
    [updateVentureField]
  )

  const updateConstraints = useCallback(
    (nodeId: string, value: string) => {
      updateVentureField(nodeId, 'hardConstraints', value)
    },
    [updateVentureField]
  )

  const updateGoals = useCallback(
    (nodeId: string, value: string) => {
      updateVentureField(nodeId, 'goals', value)
    },
    [updateVentureField]
  )

  const updateProductType = useCallback(
    (nodeId: string, value: string) => {
      updateVentureField(nodeId, 'productType', value)
    },
    [updateVentureField]
  )

  return {
    updateVentureName,
    updateBusinessModel,
    updateCapabilities,
    updateConstraints,
    updateGoals,
    updateProductType,
  }
}
